import { computed, EventEmitter, inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import { filter } from "rxjs/operators";
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

import { getMainNavRoutes } from '../../core/main-nav-routes';
import { SecurityService } from '../../core/security/security.service';
import { GlobalVariablesService } from '../../services/global-variables.service';
import { FormatHelperService } from '../helpers/format-helper.service';
import { GlobalService } from '../service/global.service';
import { NavRoute } from "./nav.route.interface";

export const NavDividerCharacter = '>>';

const rootNav = { name: "Root", id: "root" }

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class NavigationService {

    private breakpointObs = inject(BreakpointObserver);

    selectedMainNav = signal<string>(null!);
    isNavOpen = signal<boolean>(false);
    isNavFixed = signal<boolean>(false);
	isMobile = signal<boolean>(false);
	userProfileExpanded = signal<boolean>(false);

	// The currently OPEN primary navigation menu
	currOpenPrimaryNavMenu = signal<string>("");
	currOpenPrimaryNavMenu$ = toObservable(this.currOpenPrimaryNavMenu);

	// The currently OPEN secondary navigation menu
	currOpenSecondaryNavMenu = signal<NavRoute>(rootNav);
	currOpenSecondaryNavMenu$ = toObservable(this.currOpenSecondaryNavMenu);

	// The currently OPEN side navigation menu
	currOpenSideNavMenu = signal<NavRoute>(rootNav);
	currOpenSideNavMenu$ = toObservable(this.currOpenSideNavMenu);

	// The currently SELECTED navigation menu
	currSelectedMainNavMenu = signal<NavRoute>(rootNav);
	currSelectedMainNavMenu$ = toObservable(this.currSelectedMainNavMenu);

	// Force select an item in nav
	currForceSelect = signal<string>(null!);
	currForceSelect$ = toObservable(this.currForceSelect);

	navRoutes = signal<NavRoute[]>(getMainNavRoutes());
	navRoutes$ = toObservable(this.navRoutes);

	currNav = signal<NavRoute>(null!);

    dynamicNavTitle = computed(() => {
		switch (this.currNav()?.name) {
			case 'Event': return this.gvs.eventName();
			case 'Organization': return this.gvs.orgName();
            case 'Site' : return this.gvs.siteName()
            case 'User': return this.gvs.userName();
			default: return FormatHelperService.GetIsNullyOrWhitespace(this.currNav()?.name) ? 'EMIT' : this.currNav().name;
		}
	});

    lastPageInitiatedMenu = signal<string[]>(null!);

    public homeButtonClicked$: EventEmitter<boolean>;

	constructor(
		public gs: GlobalService
        , public gvs: GlobalVariablesService
        , private router: Router
        , private sec: SecurityService 
        , title: Title  
	) {
		router.events.pipe(filter((event) => event instanceof NavigationEnd))
			.subscribe((event: NavigationEnd) => {
                if (sec.isLoggedOn()) {
					setNavRoutes();
					this.parseUrl(event.url);
					setTitle();
				}
				/* 
                const menu = this.getMenuByUrl(event?.url);
				this.currNav.set(menu!);
				setNavRoutes();
				this.parseUrl(event.url);
                */
			}
		);

        const setTitle = () => {
			if (
                title.getTitle() === "EMIT" 
                || FormatHelperService.GetIsNullyOrWhitespace(title.getTitle())
            ) {	title.setTitle(this.dynamicNavTitle() ?? "EMIT"); }
		};

		// Update nav routes on security and user updates
		const setNavRoutes = () => {
			// Set nav routes
			const routes = sec.getSecureNavItems(getMainNavRoutes());
			this.navRoutes.set(routes);
			// Try to reload current nav to fix children
			const currNav = this.currSelectedMainNavMenu();
			const newSelectedMainNavMenu = this.getFlatMenu(routes).find((o) => o.id === currNav?.id);
			if (newSelectedMainNavMenu) { this.currSelectedMainNavMenu.set(newSelectedMainNavMenu); }
		};
		sec.user$.subscribe(setNavRoutes);
		sec.security$.subscribe(setNavRoutes);
		this.isNavFixed.set(localStorage.getItem("isNavFixed") == "true");
        this.setIsMobile();
        this.homeButtonClicked$ = new EventEmitter();
	}

	/**
	 * Set the currently open navigation menu.
	 * On pages, use setOpenSecondaryMenu instead.
	 * @param currMainNavMenu The current main nav menu as a dot separated string. Always starts with
	 * root. For example, 'root>>Style Guide>>Templates'
	 * @param isSecondary Whether or not to open secondary menu instead of primary menu
	 * @param isSideNav Whether or not to open side menu instead of primary menu
    */
	setCurrOpenNavMenu(
		newMenu: string
		, isSecondary: boolean = false
		, isSideNav: boolean = false
	) {
		if (
			!FormatHelperService.GetIsNullyOrWhitespace(newMenu) 
			&& (
				isSecondary 
				|| isSideNav
			)
		) {
			const nextNav = this.getFlatMenu(this.sec.getSecureNavItems(getMainNavRoutes())).find(nav => nav.id === newMenu);
			if (isSecondary) { this.currOpenSecondaryNavMenu.set(nextNav!); } 
			else if (isSideNav) { this.currOpenSideNavMenu.set(nextNav!); }
		} else { this.currOpenPrimaryNavMenu.set(newMenu); }
	}

	/** Used to set active menu for pages */
	setActiveNav(newMenu: string[]) { this.setOpenSecondaryMenu(newMenu); }

	/** Used to set secondary menu for pages */
	setOpenSecondaryMenu(newMenu: string[]) {
		if (newMenu?.length === 0) { newMenu = this.lastPageInitiatedMenu(); }
		if (
			!FormatHelperService.GetIsNully(newMenu) 
			&& newMenu?.length > 1
		) { this.lastPageInitiatedMenu.set(newMenu); }
		
		const menuList = this.getMenuNameWithRoot(newMenu);
		const flatMenu = this.getFlatMenu(this.sec.getSecureNavItems(getMainNavRoutes()));
		let menuId = !menuList 
			? "" 
			: menuList.join(NavDividerCharacter);	
			
		if (!FormatHelperService.GetIsNullyOrWhitespace(menuId)) {
			const menuItem = flatMenu.find(o => o.id === menuId);	
			if (
				menuItem?.path 
				&& newMenu?.length > 0
			) {
				this.currForceSelect.set(menuId);
				menuId = menuList.splice(0, newMenu.length).join(NavDividerCharacter);
			} else { this.currForceSelect.set(null!); }
			this.setCurrOpenNavMenu(menuId, true);
		}
	}

	/** Used to set side menu for pages */
	setOpenSideMenu(newMenu: string[]) {
		const menuList = this.getMenuNameWithRoot(newMenu);
		this.setCurrOpenNavMenu(menuList.join(NavDividerCharacter), false, true);
	}

	/** Return a flattened menu that can more easily be filtered and searched */
	getFlatMenu(
		routes: NavRoute[]
		, id = 'root'
		, parentNav: NavRoute = null!
	): NavRoute[] {
		let flatRoutes: NavRoute[] = [];
		// For each passed in route...
		for (let i = 0; i < routes?.length; ++i) {
            if (routes[i] != undefined) {
                // Set an id, which will be dot separated and begin with root
                // for example, root.Style Guide.Icons
                routes[i].id = id + NavDividerCharacter + routes[i].name;
                // Save the parent navigation menu
                routes[i].parentNav = parentNav;
                // If we have children...
                // Recursively call this function and add child routes to list
                if (routes[i]?.children) { 
                    flatRoutes.push(
                        ...this.getFlatMenu(
                            routes[i]?.children!
                            , routes[i].id
                            , routes[i]
                        )
                    ); 
                }
                // Add the current route to the nav
                flatRoutes.push(routes[i]);
            }
		}
		return flatRoutes;
	}

	/**
	 * Find a menu by URL.
	 * @param url The URL to search by
    */
	getMenuByUrl(url: string) {
		// An empty url indicates we are in the root nav
		if (url === '') { return null; }

		const flatMenu = this.getFlatMenu(this.sec.getSecureNavItems(getMainNavRoutes()));
		
		let myRoute = flatMenu.find((o) =>
			(
				('/' + (o.href ?? o.path)) === url 
				&& o.exact
			) 
			|| (
				(o.href ?? o.path) 
				&& url.startsWith('/' + (o.href ?? o.path)) 
				&& !o.exact
			)
		);
		
		if (!myRoute) {
			let urlSegments = '';
			url.split("/").filter((o) => o.length > 0).forEach((o) => {
				urlSegments += o + "/";
				let temp = flatMenu.filter((m) => (m.href ?? m.path)?.startsWith(urlSegments));
				if (temp.length > 0) { myRoute = temp[temp.length - 1]; }
			});
			return myRoute;
		}

		if (myRoute?.parentNav) { myRoute = myRoute.parentNav; }

		return myRoute; // If nothing was found, return undefined
	}

	getCurrRouteIndex(navRoutes: NavRoute[]) {
		return navRoutes?.map((o, i) => (
			{
				name: o.name
				, id: o.id
				, i: o.index ?? i
				, parent: o.parentNav
				, activeNavLink: o.activeNavLink
			}
		))?.find((o) =>
			o?.id === this.currNav()?.parentNav?.id 
			|| o?.id === this.currForceSelect()
		)?.i;
	}

	/**
	 * Used to determine whether or not route link
	 * should show in top and secondary navigations
    */
	shouldShowRouteLink(
		route: NavRoute
		, navRoutes: NavRoute[]
		, index: number = null!
	) {
		return (
			route 
			&& (
				route.path 
				|| route.path === ''
			) 
			&& this.shouldDisplay(route, navRoutes, index)
		);
	}

    shouldDisplay(
		route: NavRoute
		, navRoutes: NavRoute[]
		, index: number = null!
	) { 
		return (
			(
				route?.children == null 
				|| route?.children?.length > 0
			) && (
				!route?.shouldDisplay 
				|| route?.shouldDisplay(index ?? this.getCurrRouteIndex(navRoutes))
			)
		);
	}

	toggleNavFixed(event?: boolean) {
		this.isNavFixed.set(event ?? !this.isNavFixed());
		localStorage.setItem("isNavFixed", this.isNavFixed().toString());
		this.gs.isNavFixed.next(this.isNavFixed());
		if (
			!this.isNavFixed() 
			&& !this.isNavOpen()
		) { this.collapseAllSubNavs(); }
		this.toggleNavOpen(event);
	}

	toggleNavOpen(event?: boolean) {
		this.isNavOpen.set(event ?? !this.isNavOpen());
		this.gs.isNavOpen.set(this.isNavOpen());
		if (
			!this.isNavFixed() 
			&& !this.isNavOpen()
		) { this.collapseAllSubNavs(); }
	}

	toggleSubNav(navRoute: NavRoute) {
		if (navRoute.expanded) { this.collapseSubNavRecursively(navRoute); } 
		else {
			navRoute.expanded = true;
			this.collapseAllSubNavs(navRoute);
		}
	}

	collapseAllSubNavs(
		keepOpen?: NavRoute
		, navRoutes?: NavRoute[]
	) {
		// Default to navRoutes signal if nothing is passed in
		navRoutes ??= this.navRoutes();

		// Collapse all sub navs except keepOpen if passed in
		navRoutes.filter((o) => o?.expanded)?.forEach((navItem) => {
			// Getting the entire navigation tree for the current navItem.
			let tree = this.getNavTreeRecursively(navItem);

			// If keepOpen is not in the tree, collapse the navItem and all sub navs.
			if (!tree.includes(keepOpen!)) { this.collapseSubNavRecursively(navItem); }
			// If keepOpen is in the tree, keep the navItem open and run this function again to close any sub nav siblings.
			else if (navItem?.children?.length! > 0) { this.collapseAllSubNavs(keepOpen, navItem.children); }
		});
		this.userProfileExpanded.set(false);
	}

	collapseSubNavRecursively(route: NavRoute) {
		route.expanded = false;
		route.children?.forEach((o) => { this.collapseSubNavRecursively(o); });
	}

	getNavTreeRecursively(navRoute: NavRoute) {
		const navTree: NavRoute[] = [];
		navTree.push(navRoute);
		navRoute?.children?.forEach((o) => { navTree.push(...this.getNavTreeRecursively(o)); });
		return navTree;
	}

	canViewNav(route: NavRoute): boolean {
		let hasAccess = (route?.children?.length ?? 0) === 0; // If no children, return true
		route?.children?.forEach((o) => { if (this.canViewNav(o)) { hasAccess = true; } });
		return hasAccess;
	}

	parseUrl(url: string) {
		let currentNav: NavRoute = null!;

		if (url.startsWith("/")) { url = url.slice(1); }
		
		this.navRoutes().forEach((navItem) => {
			if (url.startsWith(navItem?.area!)) {
				this.selectedMainNav.set(navItem.name);
				this.currSelectedMainNavMenu.set(navItem);
				
				if (
					url == navItem.path 
					|| url == navItem.href
				) { currentNav = navItem; }
			} 
			
			if (
				navItem?.children 
				&& url !== navItem.path 
				&& url != navItem.href
			) {
				let routes = this.getFlatMenu([navItem])?.filter(o => url.startsWith(o.activeNavLink!));

				if (routes.length > 0) {
					let route = routes[0];
					this.currForceSelect.set(routes[0]?.id!);
					currentNav = route.parentNav ? route.parentNav : route;
				}
			}
		});

		if (
			url === `users/edit/details/${this.sec?.getUser()?.id}`
			|| url === `users/edit/associate-sites/${this.sec?.getUser()?.id}`
			|| url === `users/edit/security/${this.sec?.getUser()?.id}`
			|| url === `users/edit/mou/${this.sec?.getUser()?.id}`
			|| url === `users/edit/login-history/${this.sec?.getUser()?.id}`
			|| url === `users/edit/audit-history/${this.sec?.getUser()?.id}`
			|| url === `users/edit/notifications/${this.sec?.getUser()?.id}`
		) { 
			this.collapseAllSubNavs();
			this.selectedMainNav.set("profile"); 
		}

		if (
			url === "dashboard" 
			|| url === "/"
		) { this.selectedMainNav.set("Home"); }

		this.currNav.set(currentNav);
	}

    homeButtonClick() {
        this.homeButtonClicked$.emit(true);
        this.router.navigateByUrl('dashboard');
    }

    setIsMobile() {
        this.breakpointObs.observe([
            Breakpoints.Handset
            , Breakpoints.XSmall
            , Breakpoints.Small
            , Breakpoints.Handset
            , Breakpoints.HandsetPortrait
            , Breakpoints.TabletPortrait
            , Breakpoints.HandsetLandscape
        ]).subscribe((result) => { this.isMobile.set(result.matches); });
    }

	/** Used in the function above to fix array for functions above */
	private getMenuNameWithRoot(newMenu: string[]) {
		let menuList = ['root'];
		if (
			!FormatHelperService.GetIsNully(newMenu) 
			&& newMenu[0] !== "root"
		) { menuList = menuList.concat(newMenu); } 
		else { menuList = newMenu; }
		return menuList;
	}

    private toggleNavOpenDebounce(event?: boolean) {		
		event = (event ?? !this.isNavOpen()) || this.isNavFixed();

		if (event != this.isNavOpen()) {
			this.isNavOpen.set(event);
			this.gs.isNavOpen.set(event);
			if (!event) { this.collapseAllSubNavs(); }
		}	
	}
}
